import React, { useMemo } from 'react';
import type { ChangeOrder, EnhancedDeal, SalesOrder } from '../../types';
import type { CashflowMonthlyTotals } from '../../types/finances';
import type { ForecastBucket } from '../../utils/financesForecast';
import type { ReadyToBillRow } from '../../utils/readyToBill';
import type { ComputedBonusPlan } from '../../utils/bonusPlan';
import type { TaxPlanBreakdown } from '../../utils/taxPlan';
import {
  crmRemainingBurn,
  crmSoldInYear,
  crmStillToBillInYear,
  crmUnbilledBacklog,
  impliedTaxableClose,
  qbYearToDate,
  remainingJobOutflowsInYear,
  type ProfitOutflowMonth,
} from '../../utils/profitPlan';
import { formatScenarioMoney } from '../../utils/cashFlowScenario';
import ProfitShareBar from './ProfitShareBar';

export interface CockpitProfitInputs {
  qbMonths?: string[];
  qbTotals?: Record<string, CashflowMonthlyTotals>;
  moneyInRows: ReadyToBillRow[];
  buckets: ForecastBucket[];
  deals: EnhancedDeal[];
  salesOrders: Array<SalesOrder & { id: string }>;
  changeOrders: ChangeOrder[];
  /** Live forecast + approved CapEx months (not draft stacks). */
  projectionMonths?: ProfitOutflowMonth[];
}

interface ProfitPlannerPanelProps {
  year: number;
  years: number[];
  onYearChange: (year: number) => void;
  estimatedProfit: number;
  onProfitChange: (profit: number) => void;
  profitHint: number | null;
  taxBreakdown: TaxPlanBreakdown | null;
  bonusComputed: ComputedBonusPlan | null;
  baselineLowest: number | null;
  draftLowest: number | null;
  busy?: boolean;
  error?: string | null;
  onSave: () => void;
  inputs: CockpitProfitInputs;
}

const ProfitPlannerPanel: React.FC<ProfitPlannerPanelProps> = ({
  year,
  years,
  onYearChange,
  estimatedProfit,
  onProfitChange,
  profitHint,
  taxBreakdown,
  bonusComputed,
  baselineLowest,
  draftLowest,
  busy = false,
  error = null,
  onSave,
  inputs,
}) => {
  const now = useMemo(() => new Date(), []);
  const qb = useMemo(
    () => qbYearToDate(year, inputs.qbMonths, inputs.qbTotals),
    [inputs.qbMonths, inputs.qbTotals, year],
  );
  const sold = useMemo(
    () => crmSoldInYear(year, inputs.deals, inputs.salesOrders, inputs.changeOrders),
    [inputs.changeOrders, inputs.deals, inputs.salesOrders, year],
  );
  const stillToBill = useMemo(
    () => crmStillToBillInYear(year, inputs.moneyInRows, now),
    [inputs.moneyInRows, now, year],
  );
  const backlog = useMemo(
    () => crmUnbilledBacklog(inputs.moneyInRows),
    [inputs.moneyInRows],
  );
  const remainingBurn = useMemo(
    () => crmRemainingBurn(year, inputs.buckets, now),
    [inputs.buckets, now, year],
  );
  const remainingOut = useMemo(
    () => remainingJobOutflowsInYear(year, inputs.projectionMonths ?? [], now),
    [inputs.projectionMonths, now, year],
  );
  const implied = impliedTaxableClose({
    qbYtdNet: qb.net,
    stillToBill,
    remainingBurn,
    remainingAp: remainingOut.ap,
    remainingJobCosts: remainingOut.job,
    remainingCapex: remainingOut.capex,
  });
  const gap = estimatedProfit - implied;
  const lowestDelta =
    baselineLowest != null && draftLowest != null ? draftLowest - baselineLowest : null;

  return (
    <div className="fin-obl-embed">
      <p className="fin-modal-help">
        QuickBooks income is progress billing — when we invoiced, not what we sold.
        CRM holds sold work and the remaining schedule. Implied close now also
        subtracts open supplier bills, leftover job costs, and approved CapEx
        from the cash timeline (not draft CapEx, bonuses, or tax). This target
        feeds bonuses and tax. It does not write Sequence or QuickBooks.
      </p>
      <div className="fin-obl-body fin-obl-body--embedded">
        <div className="fin-obl-toolbar">
          <label className="fin-modal-field">
            <span className="fin-modal-label">Year</span>
            <select
              className="fin-modal-select"
              value={year}
              onChange={(e) => onYearChange(Number(e.target.value))}
            >
              {years.map((y) => (
                <option key={y} value={y}>{y}</option>
              ))}
            </select>
          </label>
        </div>

        <div className="fin-obl-kpis">
          <div className="fin-kpi-card">
            <span className="fin-kpi-label">Sold this year (CRM)</span>
            <span className="fin-kpi-value">{formatScenarioMoney(sold.total)}</span>
            <span className="fin-kpi-sub">
              Signed {formatScenarioMoney(sold.contractSigned)}
              {sold.changeOrders ? ` · COs ${formatScenarioMoney(sold.changeOrders)}` : ''}
            </span>
          </div>
          <div className="fin-kpi-card">
            <span className="fin-kpi-label">Billed this year (QB)</span>
            <span className="fin-kpi-value">{formatScenarioMoney(qb.income)}</span>
            <span className="fin-kpi-sub">Progress billings · not bookings</span>
          </div>
          <div className="fin-kpi-card">
            <span className="fin-kpi-label">Unbilled backlog (CRM)</span>
            <span className="fin-kpi-value">{formatScenarioMoney(backlog)}</span>
            <span className="fin-kpi-sub">Open schedule rows not yet invoiced</span>
          </div>
        </div>

        <div className="fin-obl-kpis">
          <div className="fin-kpi-card">
            <span className="fin-kpi-label">QB YTD net</span>
            <span className="fin-kpi-value">{formatScenarioMoney(qb.net)}</span>
            <span className="fin-kpi-sub">
              COGS {formatScenarioMoney(qb.cogs)} · opex {formatScenarioMoney(qb.expense)}
            </span>
          </div>
          <div className="fin-kpi-card">
            <span className="fin-kpi-label">Still to bill this year</span>
            <span className="fin-kpi-value">{formatScenarioMoney(stillToBill)}</span>
            <span className="fin-kpi-sub">CRM schedule remaining in {year}</span>
          </div>
          <div className="fin-kpi-card">
            <span className="fin-kpi-label">Remaining burn</span>
            <span className="fin-kpi-value">{formatScenarioMoney(remainingBurn)}</span>
            <span className="fin-kpi-sub">Overhead + payroll still in {year}</span>
          </div>
          <div className="fin-kpi-card">
            <span className="fin-kpi-label">Remaining AP</span>
            <span className="fin-kpi-value">{formatScenarioMoney(remainingOut.ap)}</span>
            <span className="fin-kpi-sub">Open supplier bills still in {year}</span>
          </div>
          <div className="fin-kpi-card">
            <span className="fin-kpi-label">Remaining job costs</span>
            <span className="fin-kpi-value">{formatScenarioMoney(remainingOut.job)}</span>
            <span className="fin-kpi-sub">Design / install / freight / travel leftover</span>
          </div>
          <div className="fin-kpi-card">
            <span className="fin-kpi-label">Approved CapEx</span>
            <span className="fin-kpi-value">{formatScenarioMoney(remainingOut.capex)}</span>
            <span className="fin-kpi-sub">Approved plans still in {year} · drafts stay off</span>
          </div>
          <div className="fin-kpi-card">
            <span className="fin-kpi-label">Implied close</span>
            <span className="fin-kpi-value">{formatScenarioMoney(implied)}</span>
            <span className="fin-kpi-sub">QB net + still to bill − burn − AP − job − CapEx</span>
          </div>
        </div>

        <div className="fin-obl-grid">
          <label className="fin-modal-field">
            <span className="fin-modal-label">Planning target (net profit)</span>
            <input
              className="fin-modal-input"
              type="number"
              min={0}
              step={1000}
              value={estimatedProfit || ''}
              onChange={(e) => onProfitChange(Number(e.target.value) || 0)}
              aria-label="Year-end profit target"
            />
            <button
              type="button"
              className="fin-obl-hint"
              onClick={() => onProfitChange(implied)}
            >
              Use implied close ({formatScenarioMoney(implied)})
            </button>
            {profitHint != null && (
              <button
                type="button"
                className="fin-obl-hint"
                onClick={() => onProfitChange(profitHint)}
              >
                Use last 12 mo QB net — books only ({formatScenarioMoney(profitHint)})
              </button>
            )}
          </label>
          <div className="fin-modal-field">
            <span className="fin-modal-label">Gap vs implied</span>
            <span className={`fin-kpi-value${gap < 0 ? ' fin-planning-danger' : ''}`}>
              {gap === 0 ? 'On implied' : `${gap > 0 ? '+' : ''}${formatScenarioMoney(gap)}`}
            </span>
            <span className="fin-kpi-sub">
              {gap === 0
                ? 'Target matches the CRM + QB close.'
                : gap > 0
                  ? 'Target is above the implied close — need more billed work or less spend.'
                  : 'Target is below the implied close.'}
            </span>
          </div>
        </div>

        <ProfitShareBar
          profit={estimatedProfit}
          bonusPool={bonusComputed?.bonusPool ?? 0}
          employerTax={bonusComputed?.employerTaxTotal ?? 0}
          taxCash={taxBreakdown?.companyCashTotal ?? 0}
          emptyHint="Set a year-end profit target to see W-2, bonuses, and tax cash."
        />

        {lowestDelta != null && lowestDelta !== 0 && (
          <p className="fin-modal-help">
            Cash floor check: lowest projected cash
            {' '}
            {draftLowest != null ? formatScenarioMoney(draftLowest) : '—'}
            {baselineLowest != null && (
              <> ({lowestDelta > 0 ? '+' : ''}{formatScenarioMoney(lowestDelta)} vs saved plan)</>
            )}
            . Bonus and tax dates still drive the cash line.
          </p>
        )}

        {error && <div className="fin-modal-error">{error}</div>}
        <div className="fin-cockpit-panel-actions">
          <button type="button" className="fin-primary-btn" onClick={onSave} disabled={busy}>
            Save profit plan
          </button>
        </div>
      </div>
    </div>
  );
};

export default ProfitPlannerPanel;
